'use client';

import { useEffect, useState, useTransition } from 'react';
import { forgotPassword } from '@/features/auth/forgot-form/api/forgot-password';
import { LOGIN_PATHNAME } from '@/shared/constants/pathnames';
import { EnvelopeIcon } from '@phosphor-icons/react/ssr';
import { Button, Text } from '@/shared/ui/core';
import styles from './recovery-email-sent.module.scss';


const RESEND_COOLDOWN_SECONDS = 60;

type ComponentProps = {
    email: string
}


export const RecoveryEmailSent = ({ email }: ComponentProps) => {
    const [ secondsLeft, setSecondsLeft ] = useState(RESEND_COOLDOWN_SECONDS);
    const [ isPending, startTransition ]  = useTransition();

    useEffect(() => {
        if (secondsLeft <= 0) return;

        const intervalId = setInterval(() => {
            setSecondsLeft((prev) => Math.max(0, prev - 1));
        }, 1000);

        return () => clearInterval(intervalId);
    }, [ secondsLeft ]);

    const handleResend = () => {
        if (!email) return;

        startTransition(async () => {
            const result = await forgotPassword({ email });
            if (result.ok) setSecondsLeft(RESEND_COOLDOWN_SECONDS);
        });
    };

    const isDisabled = secondsLeft > 0 || isPending || !email;

    return (
        <div
            data-template='recovery-email-sent'
            className={ styles.controls }
        >
            <Button
                disabled={ isDisabled }
                onClick={ handleResend }
                leftIcon={ <EnvelopeIcon size={ 16 }/> }
                className={ styles.button }
            >
                Resend recovery email
                {
                    !!secondsLeft &&
                    <span className={ styles.time }>
                        &nbsp;({ secondsLeft.toString().padStart(2, '0') })
                    </span>
                }
            </Button>

            <Text
                as='span'
                size='lg'
                tone='secondary'
            >
                or
            </Text>

            <Button
                as='a'
                href={ LOGIN_PATHNAME }
                className={ styles.button }
            >
                Log in
            </Button>
        </div>
    );
};
